import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { NavigationContextType, NavigationState, BreadcrumbItem } from '../types/navigation';

/**
 * Navigation Context Provider for breadcrumb management
 * Storyboard Legal - UX Enhancement Phase 1
 * 
 * Provides centralized navigation state management following the same pattern
 * as AuthContext for consistency with existing codebase architecture.
 */

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

interface NavigationProviderProps {
  children: ReactNode;
}

const initialState: NavigationState = {
  breadcrumbs: [],
  pageTitle: '',
  isLoading: false
};

/**
 * Navigation provider that manages breadcrumb state throughout the application
 * Implements centralized navigation management for consistent UX
 */
export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const [state, setState] = useState<NavigationState>(initialState);

  /**
   * Set breadcrumbs for the current page
   * Automatically marks the last item as current
   */
  const setBreadcrumbs = useCallback((breadcrumbs: BreadcrumbItem[]) => {
    const updatedBreadcrumbs = breadcrumbs.map((item, index) => ({
      ...item,
      current: index === breadcrumbs.length - 1
    }));

    setState(prev => ({
      ...prev,
      breadcrumbs: updatedBreadcrumbs
    }));
  }, []);

  /**
   * Set the page title
   */
  const setPageTitle = useCallback((title: string) => {
    setState(prev => ({
      ...prev,
      pageTitle: title
    }));
    
    // Update document title for browser tab
    document.title = title ? `${title} - Storyboard Legal` : 'Storyboard Legal';
  }, []);

  /**
   * Set loading state for async navigation updates
   */
  const setIsLoading = useCallback((loading: boolean) => {
    setState(prev => ({
      ...prev,
      isLoading: loading
    }));
  }, []);

  /**
   * Clear navigation state (useful for logout or route changes)
   */
  const clearNavigation = useCallback(() => {
    setState(initialState);
    document.title = 'Storyboard Legal';
  }, []);

  const contextValue: NavigationContextType = {
    state,
    setBreadcrumbs,
    setPageTitle,
    setIsLoading,
    clearNavigation
  };

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};

/**
 * Hook to access navigation context
 * Throws error if used outside of NavigationProvider
 */
export const useNavigation = (): NavigationContextType => {
  const context = useContext(NavigationContext);
  
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  
  return context;
};

/**
 * Utility hook for setting page navigation
 * Simplifies common navigation setup patterns
 */
export const usePageNavigation = () => {
  const { setBreadcrumbs, setPageTitle, setIsLoading } = useNavigation();

  /**
   * Set up complete page navigation in one call
   */
  const setPageNavigation = useCallback((
    title: string,
    breadcrumbs: BreadcrumbItem[],
    isLoading: boolean = false
  ) => {
    setPageTitle(title);
    setBreadcrumbs(breadcrumbs);
    setIsLoading(isLoading);
  }, [setBreadcrumbs, setPageTitle, setIsLoading]);

  return {
    setBreadcrumbs,
    setPageTitle,
    setIsLoading,
    setPageNavigation
  };
};
